import type { components } from './schema.gen.js';

/**
 * The API's error envelope, taken straight from the generated OpenAPI types.
 *
 * Nothing here is hand-written. Add an error code on the server and it appears
 * in this union the next time `npm run api:types` runs, so a `switch` that has
 * stopped being exhaustive becomes a compile error rather than a silent
 * fall-through to "something went wrong".
 */
export type ApiErrorBody = components['schemas']['ApiError'];
export type ErrorCode = ApiErrorBody['error']['code'];
export type FieldIssue = components['schemas']['FieldIssue'];

/**
 * A failed API call.
 *
 * Carries the server's stable `code`, so UI decisions are made on the code and
 * never on message text — messages are written for humans and are free to
 * change without breaking anything.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly issues: FieldIssue[];
  readonly requestId: string;
  /**
   * Which face check refused, where one did — one of `FACE_FAILURES`.
   *
   * Here so the check-in flow can tell apart a refusal worth trying again
   * for, such as a blink that fell between two frames, from one that never
   * will, such as a face that is not the account holder.
   */
  readonly failure: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.issues = body.error.issues ?? [];
    this.requestId = body.error.requestId;
    this.failure = body.error.failure ?? null;
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }

  /** True for any of the given codes. Keeps call sites readable. */
  hasCode(...codes: ErrorCode[]): boolean {
    return codes.includes(this.code);
  }

  /**
   * Field errors keyed by path, ready to hand to an Ant Design form.
   *
   * Attaching the message to the offending input — rather than showing one
   * banner at the top — is what lets someone fix a 30-field onboarding form
   * without hunting for which field the server objected to.
   */
  get fieldErrors(): Record<string, string[]> {
    const grouped: Record<string, string[]> = {};
    for (const issue of this.issues) {
      (grouped[issue.path] ??= []).push(issue.message);
    }
    return grouped;
  }
}

/**
 * The network never answered: offline, DNS failure, CORS, or the server is
 * down. Distinct from `ApiError` because the advice is different — "check your
 * connection" rather than "fix this field".
 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('We could not reach the server. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** Codes that mean "this session is over" — the client should return to sign-in. */
export const SESSION_ENDED_CODES: readonly ErrorCode[] = [
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
];

export function isSessionEnded(error: unknown): boolean {
  return ApiError.is(error) && SESSION_ENDED_CODES.includes(error.code);
}

/** A user-facing sentence for any thrown value, however unexpected. */
export function messageFor(error: unknown): string {
  if (ApiError.is(error) || error instanceof NetworkError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}
